import { AddressComponents } from "components/AddressComponent/AddressComponent";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { Form, Formik, FormikHelpers } from "formik";
import { addFilingManualBankSchema } from "formValidations/AddFilingManualBankSchema";
import { useToast } from "hooks/useToast";
import {
  useCreateAutofillMutation,
  useUpdateAutofillMutation,
} from "store/apis/autofill";
import {
  AutofillComponentProps,
  BankAutofill,
} from "types/Models/bankAutofill";
import { BackendError } from "types/utils/error";
import FilingInfoIcon from "static/images/FilingInfo.svg";

type AddManualBankValues = {
  name: string;
  account_number: string;
  routing_number: string;
  address: string;
  bank_details_entity_name: string;
};

export const AddFilingManualBank = ({
  isOpenEdit,
  onClose,
  entityId,
  initialValue,
  groupId,
  onSuccess,
}: AutofillComponentProps & {
  initialValue?: BankAutofill;
  onSuccess?: (value: BankAutofill) => void;
}) => {
  const bankAutofillKey = "bank_accounts";
  const { alertToast, successToast } = useToast();
  const [createAutofill, { isLoading: isAdding }] = useCreateAutofillMutation();
  const [editAutofill, { isLoading: isUpdating }] = useUpdateAutofillMutation();

  const addBank = async (
    values: AddManualBankValues,
    { resetForm }: FormikHelpers<AddManualBankValues>
  ) => {
    try {
      if (entityId) {
        const bank = (await createAutofill({
          groupId,
          entityId,
          autofillKey: bankAutofillKey,
          fields: {
            account_number: values.account_number,
            name: values.name,
            routing_number: values.routing_number,
            entity_id: entityId,
            address_autofill_id: values.address,
            bank_details_entity_name: values.bank_details_entity_name,
          },
        }).unwrap()) as BankAutofill;
        successToast({ message: "Bank details added" });
        resetForm();
        onSuccess?.(bank);
        onClose();
      }
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const editBank = async (
    values: AddManualBankValues,
    { resetForm }: FormikHelpers<AddManualBankValues>
  ) => {
    try {
      if (entityId) {
        const bank = (await editAutofill({
          groupId,
          entityId,
          autofillKey: bankAutofillKey,
          fields: {
            account_number: values.account_number,
            name: values.name,
            routing_number: values.routing_number,
            entity_id: entityId,
            address_autofill_id: values.address,
            bank_details_entity_name: values.bank_details_entity_name,
          },
          uuid: initialValue?.uuid!,
        }).unwrap()) as BankAutofill;
        successToast({ message: "Bank details updated" });
        resetForm();
        onSuccess?.(bank);
        onClose();
      }
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const initialValueForEdit = {
    name: initialValue?.name!,
    account_number: initialValue?.account_number!,
    routing_number: initialValue?.routing_number!,
    address: initialValue?.address || "",
    bank_details_entity_name: initialValue?.bank_details_entity_name || "",
  };

  const initialValueForAdd = {
    name: "",
    account_number: "",
    routing_number: "",
    address: "",
    bank_details_entity_name: "",
  };
  const isEditingFlow = Boolean(
    initialValue?.uuid || initialValue?.bank_details_entity_name
  );

  return (
    <>
      <Modal.Root open={isOpenEdit} onOpenChange={onClose}>
        <Formik
          initialValues={
            isEditingFlow ? initialValueForEdit : initialValueForAdd
          }
          onSubmit={isEditingFlow ? editBank : addBank}
          validateOnChange={false}
          validationSchema={addFilingManualBankSchema}
          enableReinitialize
          validateOnBlur={false}
          validateOnMount={isEditingFlow}
          initialTouched={
            isEditingFlow
              ? { name: true, account_number: true, routing_number: true }
              : {}
          }
        >
          {({
            submitForm,
            isSubmitting,
            setFieldValue,
            values,
            errors,
            dirty,
          }) => (
            <Form>
              <Modal.Content useCustomOverlay>
                <Modal.Header>
                  <Modal.Title>
                    {isEditingFlow ? "Edit" : "Add"} Bank Account
                  </Modal.Title>
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body className="t-flex t-gap-4 t-flex-col">
                  <TextInput
                    required
                    name="name"
                    label="Bank name (As mentioned in domestic wire details)"
                  />
                  <TextInput
                    required
                    name="account_number"
                    label="Account number"
                  />
                  <TextInput
                    required
                    name="routing_number"
                    label="ACH Routing number"
                  />
                  <TextInput
                    name="bank_details_entity_name"
                    label={
                      <div className="t-flex t-gap-1 t-items-center">
                        <span>
                          Account holder’s name (Company or personal name)
                        </span>
                        <ToolTip text="This is the normally the legal name of the company, and is visible on your checkbook">
                          <img
                            src={FilingInfoIcon}
                            alt="FilingInfoIcon"
                            height="12"
                          />
                        </ToolTip>
                      </div>
                    }
                  />
                  <AddressComponents
                    formPrefix=""
                    entityId={entityId!}
                    groupId={groupId}
                    selectedAddressId={values.address}
                    label="Bank account holders address"
                    onAddressSelect={(address) => {
                      setFieldValue("address", address?.uuid || "");
                    }}
                    srollToSave
                    hideClear
                    error={errors.address}
                  />
                </Modal.Body>
                <Modal.Footer>
                  <div className="t-flex t-gap-3 t-justify-end t-w-full">
                    <Button
                      type="reset"
                      onClick={onClose}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      customType="primary"
                      onClick={submitForm}
                      isLoading={isAdding || isUpdating}
                      disabled={
                        isAdding || isUpdating || (isEditingFlow && !dirty)
                      }
                      type="submit"
                    >
                      Save
                    </Button>
                  </div>
                </Modal.Footer>
              </Modal.Content>
            </Form>
          )}
        </Formik>
      </Modal.Root>
    </>
  );
};
